import React, { useState, useEffect } from 'react';
import { CheckCircle2, ChevronRight, Clock, Target, Loader, Sparkles } from 'lucide-react';
import { API_URLS } from '../config/api';

const ExamTransition = ({
  currentExercise,
  totalExercises,
  completedExercise,
  nextExercise,
  onContinue,
  isLastExercise = false,
  studentName = 'Utilisateur' // Valeur par défaut si non fournie (pour l'affichage uniquement)
}) => {
  const [countdown, setCountdown] = useState(isLastExercise ? 0 : 3); // Pas de compte à rebours bloquant pour la fin
  const [isAnimated, setIsAnimated] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  // Récupérer l'ID utilisateur pour les appels API
  const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
  const userId = userInfo.id;

  useEffect(() => {
    setIsAnimated(true);

    if (!isLastExercise) {
      // --- LOGIQUE STANDARD (Exercices intermédiaires) ---
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            if (onContinue) onContinue();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);

    } else {
      // --- LOGIQUE DERNIER EXERCICE (Polling) ---
      // On attend un peu pour l'animation (1.5s) puis on lance le polling
      const startPollingTimer = setTimeout(() => {
        setIsPolling(true);
        startPollingBackend();
      }, 1500);

      return () => clearTimeout(startPollingTimer);
    }
  }, [isLastExercise, onContinue, userId, totalExercises]);  // Utiliser userId au lieu de studentName

  const startPollingBackend = () => {
    if (!userId) {
      console.error('❌ User ID manquant pour le polling');
      return;
    }

    const pollInterval = setInterval(() => {
      console.log(`🔍 Vérification des résultats pour l'utilisateur ${userId}...`);
      
      fetch(API_URLS.GET_EXAM_RESULTS(userId))  // Utiliser userId
        .then(res => res.json())
        .then(data => {
          console.log("📥 Status polling:", data);
          
          // Condition de sortie : On a récupéré "totalExercises" (ex: 3) résultats
          if (data.status === 'success' && data.count >= totalExercises) {
            console.log("✅ Analyse complète détectée ! Redirection...");
            clearInterval(pollInterval);
            if (onContinue) {
              // On passe les données finales complètes pour affichage direct
              onContinue(data.data);
            }
          }
        })
        .catch(err => console.error("Polling error:", err));
    }, 2000); // Vérifie toutes les 2 secondes

    // Sécurité: Arrêter le polling si le composant est démonté
    return () => clearInterval(pollInterval);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = (currentExercise / totalExercises) * 100;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-8">
      <div className={`max-w-3xl w-full transition-all duration-700 ease-out ${
        isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>

        {/* --- Section de Progression Globale --- */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-6 sm:mb-10">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">Progression de l'Évaluation</h3>
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-3 text-sm font-medium text-gray-600">
            <span>Exercice {currentExercise} / {totalExercises}</span>
            <span className="flex items-center gap-1 text-indigo-600">
              <Target className="w-4 h-4" />
              {totalExercises - currentExercise > 0 
                ? `${totalExercises - currentExercise} restant(s)` 
                : 'Terminé'}
            </span>
          </div>
        </div>

        {/* --- Message Principal --- */}
        <div className="bg-white rounded-2xl shadow-2xl shadow-indigo-100 border border-gray-100 p-6 md:p-10">
          <div className="flex flex-col items-center mb-6">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-xl transition-all duration-500 ${
               isLastExercise && isPolling ? 'bg-amber-100 animate-pulse' : 'bg-emerald-500'
            }`}>
              {isLastExercise && isPolling ? (
                <Loader className="w-8 h-8 text-amber-600 animate-spin" />
              ) : (
                <CheckCircle2 className="w-8 h-8 text-white" />
              )}
            </div>
            
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 text-center">
              {isLastExercise 
                ? (isPolling ? 'Analyse finale en cours...' : 'Évaluation Terminée') 
                : 'Exercice Validé !'}
            </h2>
            
            <p className="text-gray-500 mt-2 text-base sm:text-lg text-center">
              {isLastExercise 
                ? 'Notre IA analyse votre dernière copie pour générer le rapport complet.' 
                : 'Passage automatique à la tâche suivante.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-8">
            {/* Colonne Exercice Terminé */}
            <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-4 sm:p-5 shadow-sm">
              <h3 className="font-bold text-emerald-800 mb-2 flex items-center gap-2 text-sm sm:text-base">
                <CheckCircle2 className="w-4 h-4" />
                Dernière Tâche (N°{currentExercise})
              </h3>
              <p className="text-gray-700 font-semibold mb-1 truncate text-sm sm:text-base">
                {completedExercise?.chapter || 'Section complétée'}
              </p>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-emerald-600">
                <Clock className="w-4 h-4" />
                <span>Temps : {formatTime(completedExercise?.completionTime || 0)}</span>
              </div>
            </div>

            {/* Colonne Prochaine Étape */}
            {!isLastExercise ? (
              <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4 sm:p-5 shadow-sm">
                <h3 className="font-bold text-indigo-800 mb-2 flex items-center gap-2 text-sm sm:text-base">
                  <Target className="w-4 h-4" />
                  Prochaine Tâche (N°{currentExercise + 1})
                </h3>
                <p className="text-gray-700 font-semibold mb-1 truncate text-sm sm:text-base">
                  {nextExercise?.chapter || 'Chargement...'}
                </p>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-indigo-600 animate-pulse">
                  <ChevronRight className="w-4 h-4" />
                  <span>Démarrage dans {countdown}s</span>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 sm:p-5 shadow-sm">
                <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2 text-sm sm:text-base">
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  Génération du Bilan
                </h3>
                <p className="text-gray-700 font-semibold mb-1 text-sm sm:text-base">
                  Compilation des résultats...
                </p>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-amber-600">
                  <Loader className="w-3 h-3 animate-spin" />
                  <span>Veuillez ne pas quitter</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamTransition;